import * as htmlToImage from 'html-to-image';
import { applyGender, getCachedGender } from './genderUtils';

// Простые кэши на время сессии
const base64Cache = new Map(); // url -> base64 dataUrl

// Функция для конвертации изображения в base64 с простой оптимизацией
export const convertImageToBase64 = async (imageUrl) => {
  try {
    if (base64Cache.has(imageUrl)) return base64Cache.get(imageUrl);
    const response = await fetch(imageUrl, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const blob = await response.blob();

    // Простая оптимизация: уменьшаем разрешение до максимум 1200px по ширине
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Устанавливаем максимальную ширину 1200px для качественного шеринга
        const maxWidth = 1200;
        const aspectRatio = img.width / img.height;
        const newWidth = Math.min(img.width, maxWidth);
        const newHeight = newWidth / aspectRatio;

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Рисуем изображение с новыми размерами
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Конвертируем в JPEG с высоким качеством (0.95)
        canvas.toBlob((optimizedBlob) => {
          if (optimizedBlob) {
            console.log(`Оптимизированное изображение: ${(optimizedBlob.size / 1024).toFixed(2)} KB (${newWidth}x${newHeight})`);
            const reader = new FileReader();
            reader.onload = () => {
              base64Cache.set(imageUrl, reader.result);
              resolve(reader.result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(optimizedBlob);
          } else {
            reject(new Error('Failed to optimize image'));
          }
        }, 'image/jpeg', 0.95);
      };

      img.onerror = () => {
        // Fallback: используем оригинальный blob без оптимизации
        const reader = new FileReader();
        reader.onload = () => {
          base64Cache.set(imageUrl, reader.result);
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      };

      img.src = URL.createObjectURL(blob);
    });
  } catch (error) {
    console.error('Ошибка при конвертации изображения в base64:', error);
    return imageUrl; // Fallback на оригинальный URL
  }
};


// Функция для создания картинки из заднего фона и текущей карточки
export const createShareImage = async (activeCard, formatText, gameId = null) => {
  try {
    if (!activeCard) {
      console.error('Нет активной карточки для создания картинки');
      return null;
    }

    // Предварительно конвертируем изображение в base64
    let bgUrl = activeCard.cardPicUrl;
    try {
      bgUrl = await convertImageToBase64(activeCard.cardPicUrl);
    } catch (e) {
      console.warn('Не удалось конвертировать картинку в base64', e);
    }

    // Получаем отформатированный текст с учетом гендера
    const gender = getCachedGender();
    const q1 = formatText(applyGender(activeCard.question1, gender));
    const q2 = activeCard.question2 ? formatText(applyGender(activeCard.question2, gender)) : '';

    // === РАЗМЕРЫ ===
    const CANVAS_WIDTH = 1080;
    const CANVAS_HEIGHT = 1920;
    const CARD_WIDTH = 900;
    const CARD_HEIGHT = 1740;
    const CARD_RADIUS = 96;
    const IMAGE_PADDING = 24; // отступ изображения сверху и по бокам
    const IMAGE_RADIUS = 72;

    // Текстовый контейнер
    const TEXT_PADDING_TOP = 48;
    const TEXT_PADDING_BOTTOM = 60;
    const TEXT_PADDING_SIDES = 36;
    const GAP_BETWEEN_BLOCKS = 76; // отступ между блоком вопросов и брендингом
    const GAP_Q1_Q2 = 36; // отступ между question и subquestion

    // Шрифты
    const FONT_Q1 = 60;
    const FONT_Q2 = 48;
    const FONT_BRAND = 48;

    // ШАГ 1: Сначала измеряем высоту текстового блока
    const measureContainer = document.createElement('div');
    measureContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      z-index: -1000;
      opacity: 0;
      pointer-events: none;
      width: ${CARD_WIDTH}px;
    `;

    const measureTextBlock = document.createElement('div');
    measureTextBlock.style.cssText = `
      padding: ${TEXT_PADDING_TOP}px ${TEXT_PADDING_SIDES}px ${TEXT_PADDING_BOTTOM}px;
      display: flex;
      flex-direction: column;
      gap: ${GAP_BETWEEN_BLOCKS}px;
    `;

    // Блок вопросов для измерения
    const measureQuestionsBlock = document.createElement('div');
    measureQuestionsBlock.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: ${GAP_Q1_Q2}px;
    `;

    const measureQ1 = document.createElement('div');
    measureQ1.textContent = q1;
    measureQ1.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: ${FONT_Q1}px;
      font-weight: 700;
      line-height: 1.2;
      padding-left: 24px;
    `;
    measureQuestionsBlock.appendChild(measureQ1);

    if (q2) {
      const measureQ2 = document.createElement('div');
      measureQ2.textContent = q2;
      measureQ2.style.cssText = `
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${FONT_Q2}px;
        font-weight: 400;
        line-height: 1.3;
        padding-left: 24px;
      `;
      measureQuestionsBlock.appendChild(measureQ2);
    }

    measureTextBlock.appendChild(measureQuestionsBlock);

    const measureBrand = document.createElement('div');
    measureBrand.textContent = 'Sense — игры про тебя 🧡';
    measureBrand.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: ${FONT_BRAND}px;
      font-weight: 500;
      line-height: 1.2;
      padding-left: 24px;
    `;
    measureTextBlock.appendChild(measureBrand);

    measureContainer.appendChild(measureTextBlock);
    document.body.appendChild(measureContainer);

    // Ждем рендеринга и измеряем
    await new Promise(resolve => setTimeout(resolve, 50));
    const textBlockHeight = measureTextBlock.getBoundingClientRect().height;
    document.body.removeChild(measureContainer);

    console.log('Measured text block height:', textBlockHeight);

    // ШАГ 2: Рассчитываем высоту изображения
    // Карточка = изображение (с отступами сверху и снизу) + текстовый блок
    // IMAGE_PADDING сверху + imageHeight + IMAGE_PADDING снизу (между картинкой и текстом) + textBlockHeight = CARD_HEIGHT
    const imageHeight = CARD_HEIGHT - IMAGE_PADDING - IMAGE_PADDING - textBlockHeight;
    const imageWidth = CARD_WIDTH - (IMAGE_PADDING * 2);

    console.log('Calculated image height:', imageHeight);

    // ШАГ 3: Создаем финальную структуру
    const canvas = document.createElement('div');
    canvas.style.cssText = `
      position: relative;
      width: ${CANVAS_WIDTH}px;
      height: ${CANVAS_HEIGHT}px;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Карточка
    const card = document.createElement('div');
    card.style.cssText = `
      position: relative;
      width: ${CARD_WIDTH}px;
      height: ${CARD_HEIGHT}px;
      background: #ffffff;
      border-radius: ${CARD_RADIUS}px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 20px rgba(0, 0, 0, 0.1);
    `;

    // === КОНТЕЙНЕР ИЗОБРАЖЕНИЯ ===
    const imageContainer = document.createElement('div');
    imageContainer.style.cssText = `
      padding: ${IMAGE_PADDING}px ${IMAGE_PADDING}px ${IMAGE_PADDING}px;
      flex-shrink: 0;
    `;

    const imageWrapper = document.createElement('div');
    imageWrapper.style.cssText = `
      width: ${imageWidth}px;
      height: ${imageHeight}px;
      border-radius: ${IMAGE_RADIUS}px;
      overflow: hidden;
    `;

    const heroImg = document.createElement('img');
    heroImg.src = bgUrl;
    heroImg.style.cssText = `
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    `;

    imageWrapper.appendChild(heroImg);

    // Добавляем пунктирную обводку для игры fb49af8b-f979-4e90-8acb-a62622aa3698
    if (gameId === 'fb49af8b-f979-4e90-8acb-a62622aa3698') {
      imageWrapper.style.position = 'relative';

      const svgBorder = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svgBorder.setAttribute('width', '100%');
      svgBorder.setAttribute('height', '100%');
      svgBorder.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 1;
        overflow: visible;
      `;

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', '12');
      rect.setAttribute('y', '12');
      rect.setAttribute('width', `${imageWidth - 24}px`);
      rect.setAttribute('height', `${imageHeight - 24}px`);
      rect.setAttribute('rx', `${IMAGE_RADIUS - 12}`);
      rect.setAttribute('ry', `${IMAGE_RADIUS - 12}`);
      rect.setAttribute('fill', 'none');
      rect.setAttribute('stroke', 'white');
      rect.setAttribute('stroke-width', '24');
      rect.setAttribute('stroke-dasharray', '45 45');

      svgBorder.appendChild(rect);
      imageWrapper.appendChild(svgBorder);
    }

    imageContainer.appendChild(imageWrapper);
    card.appendChild(imageContainer);

    // === ТЕКСТОВЫЙ КОНТЕЙНЕР (прижат к низу) ===
    const textContainer = document.createElement('div');
    textContainer.style.cssText = `
      padding: ${TEXT_PADDING_TOP}px ${TEXT_PADDING_SIDES}px ${TEXT_PADDING_BOTTOM}px;
      display: flex;
      flex-direction: column;
      gap: ${GAP_BETWEEN_BLOCKS}px;
      flex-shrink: 0;
    `;

    // Блок 1: Вопросы
    const questionsBlock = document.createElement('div');
    questionsBlock.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: ${GAP_Q1_Q2}px;
    `;

    const questionEl = document.createElement('div');
    questionEl.textContent = q1;
    questionEl.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: ${FONT_Q1}px;
      font-weight: 700;
      line-height: 1.2;
      color: #000000;
      padding-left: 24px;
    `;
    questionsBlock.appendChild(questionEl);

    if (q2) {
      const subquestionEl = document.createElement('div');
      subquestionEl.textContent = q2;
      subquestionEl.style.cssText = `
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${FONT_Q2}px;
        font-weight: 400;
        line-height: 1.3;
        color: #666666;
        padding-left: 24px;
      `;
      questionsBlock.appendChild(subquestionEl);
    }

    textContainer.appendChild(questionsBlock);

    // Блок 2: Брендинг
    const brandBlock = document.createElement('div');
    brandBlock.textContent = 'Sense — игры про тебя 🧡';
    brandBlock.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: ${FONT_BRAND}px;
      font-weight: 500;
      line-height: 1.2;
      color: #000000;
      padding-left: 24px;
    `;
    textContainer.appendChild(brandBlock);

    card.appendChild(textContainer);
    canvas.appendChild(card);

    // Ждем декодирования изображения
    try {
      if (heroImg.decode) await heroImg.decode();
    } catch (e) { console.warn(e); }

    // Создаем обертку для рендеринга
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      z-index: -1000;
      opacity: 0;
      pointer-events: none;
    `;

    wrapper.appendChild(canvas);
    document.body.appendChild(wrapper);

    await new Promise(resolve => setTimeout(resolve, 200));

    // Опции для html-to-image
    const options = {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      pixelRatio: 1,
      backgroundColor: null, // прозрачный фон
      skipFonts: true,
      cacheBust: true,
      style: { transform: 'none' }
    };

    // Прогрев для Safari
    try {
      await htmlToImage.toPng(canvas, options);
    } catch (e) {
      console.log('Warmup failed', e);
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    // Генерация финальной картинки
    const dataUrl = await htmlToImage.toPng(canvas, options);

    document.body.removeChild(wrapper);

    // Оптимизируем PNG с сохранением прозрачности
    const optimizedDataUrl = await optimizePngWithTransparency(dataUrl);

    return optimizedDataUrl;
  } catch (error) {
    console.error('Ошибка при создании картинки:', error);
    throw error;
  }
};

/**
 * Оптимизирует PNG изображение с сохранением прозрачности
 * Использует canvas для перерисовки с уменьшением цветовой палитры
 */
async function optimizePngWithTransparency(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', {
        alpha: true,
        willReadFrequently: true
      });

      canvas.width = img.width;
      canvas.height = img.height;

      // Очищаем canvas с прозрачным фоном
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Рисуем изображение
      ctx.drawImage(img, 0, 0);

      // Получаем данные пикселей для оптимизации
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Квантизация цветов: уменьшаем точность цветов для лучшего сжатия
      // Это уменьшает количество уникальных цветов, что помогает PNG сжатию
      const colorStep = 4; // Чем больше, тем меньше файл, но хуже качество (2-8 оптимально)

      for (let i = 0; i < data.length; i += 4) {
        // Пропускаем полностью прозрачные пиксели
        if (data[i + 3] === 0) continue;

        // Квантизуем RGB каналы
        data[i] = Math.round(data[i] / colorStep) * colorStep;     // R
        data[i + 1] = Math.round(data[i + 1] / colorStep) * colorStep; // G
        data[i + 2] = Math.round(data[i + 2] / colorStep) * colorStep; // B
        // Alpha канал оставляем без изменений для сохранения прозрачности
      }

      ctx.putImageData(imageData, 0, 0);

      // Экспортируем оптимизированный PNG
      const optimizedDataUrl = canvas.toDataURL('image/png');

      // Логируем результат оптимизации
      const originalSize = Math.round(dataUrl.length * 0.75 / 1024); // примерный размер в KB
      const optimizedSize = Math.round(optimizedDataUrl.length * 0.75 / 1024);
      const savings = Math.round((1 - optimizedSize / originalSize) * 100);

      console.log(`PNG оптимизация: ${originalSize}KB → ${optimizedSize}KB (экономия ${savings}%)`);

      resolve(optimizedDataUrl);
    };

    img.onerror = () => {
      console.warn('Не удалось оптимизировать PNG, возвращаем оригинал');
      resolve(dataUrl);
    };

    img.src = dataUrl;
  });
}
